import { useEffect, useMemo, useState } from "react";
import { PoundSterling, CalendarDays, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PAYMENT_STYLES, STATUS_STYLES } from "@/pages/admin/Clients";

const fmt = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-[#1A237E] flex items-center justify-center">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <p className="text-sm font-medium text-slate-500">{label}</p>
      </div>
      <p className="mt-3 text-2xl font-semibold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function Revenue() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/clients")
      .then((res) => setClients(res.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const jobs = useMemo(() => clients.filter((c) => Number(c.price) > 0), [clients]);
  const total = jobs.reduce((s, c) => s + Number(c.price), 0);
  const nowKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const thisMonth = jobs
    .filter((c) => c.hire_start && c.hire_start.startsWith(nowKey))
    .reduce((s, c) => s + Number(c.price), 0);
  const outstanding = jobs
    .filter((c) => c.payment_status !== "Paid")
    .reduce((s, c) => s + Number(c.price), 0);

  const monthly = useMemo(() => {
    const buckets = {};
    for (const c of jobs) {
      if (!c.hire_start) continue;
      const key = c.hire_start.slice(0, 7);
      buckets[key] = (buckets[key] || 0) + Number(c.price);
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({
        key,
        label: new Date(`${key}-02`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
        value,
      }));
  }, [jobs]);
  const maxVal = Math.max(1, ...monthly.map((m) => m.value));

  return (
    <div data-testid="revenue-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Revenue Tracking</h1>
        <p className="text-sm text-slate-500 mt-1">
          Revenue calculated from jobs in the Client Tracker (set a price and payment status on each client).
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard Icon={PoundSterling} label="Total Revenue to Date" value={fmt(total)} sub={`${jobs.length} priced job${jobs.length === 1 ? "" : "s"}`} testid="stat-total-revenue" />
        <StatCard Icon={CalendarDays} label="Revenue This Month" value={fmt(thisMonth)} sub="Jobs starting in the current month" testid="stat-month-revenue" />
        <StatCard Icon={AlertCircle} label="Outstanding Payments" value={fmt(outstanding)} sub="Jobs not yet marked as Paid" testid="stat-outstanding" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-lg p-5" data-testid="revenue-chart">
        <h2 className="text-base font-semibold text-slate-900">Monthly Revenue</h2>
        {loading ? (
          <p className="py-10 text-center text-slate-400 text-sm">Loading…</p>
        ) : monthly.length === 0 ? (
          <p className="py-10 text-center text-slate-400 text-sm" data-testid="revenue-chart-empty">
            No revenue data yet — add a price and hire start date to a client to see the trend.
          </p>
        ) : (
          <div className="mt-5 flex items-end gap-3 h-48">
            {monthly.map((m) => (
              <div key={m.key} className="flex-1 flex flex-col items-center gap-2 min-w-0" data-testid={`revenue-bar-${m.key}`}>
                <span className="text-xs font-medium text-slate-600">{fmt(m.value)}</span>
                <div
                  className="w-full max-w-16 bg-[#1A237E] rounded-t"
                  style={{ height: `${Math.max(4, (m.value / maxVal) * 120)}px` }}
                  title={`${m.label}: ${fmt(m.value)}`}
                />
                <span className="text-xs text-slate-500">{m.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenue-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Hire Start</th>
                <th className="px-4 py-3">Hire End</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : jobs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="revenue-empty">No priced jobs yet.</td></tr>
              ) : (
                jobs.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`revenue-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.hire_start || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.hire_end || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${STATUS_STYLES[c.status] || STATUS_STYLES.Enquiry}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${PAYMENT_STYLES[c.payment_status] || PAYMENT_STYLES.Unpaid}`}>
                        {c.payment_status || "Unpaid"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(c.price)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
